/**
 * Contrat du rapport de diagnostic, et controle de coherence.
 *
 * LE CONTROLE CI-DESSOUS EST LA COPIE FIDELE de `verifier()` dans
 * `generer_rapport_html.py` (plugin cv-administratif-ats). Le generateur refuse
 * de produire un rapport dont la grille ne totalise pas les scores annonces ;
 * cette page refuse de l'afficher pour les memes raisons et avec les memes
 * seuils.
 *
 * Pourquoi le refaire ici alors que le generateur le fait deja : les donnees
 * arrivent par l'Edge Function d'ingestion, qui ne relit pas le rapport ligne a
 * ligne. Entre le generateur et cet ecran, il y a un reseau, une base et un
 * format de version. Un rapport faux presente a un apprenant en entretien est le
 * pire incident que cette application puisse produire — mieux vaut une page qui
 * dit « donnees incoherentes » qu'un score invente.
 */

/** Seule version de format rendue. Une version inconnue n'est PAS rendue. */
export const VERSIONS_RAPPORT_SUPPORTEES = ['1.0'] as const

/** Total exige par la grille, toutes colonnes confondues. */
export const TOTAL_GRILLE_ATTENDU = 100

export const COLONNES = ['max', 'actuel', 'refonte', 'certifie'] as const
export type Colonne = (typeof COLONNES)[number]

export interface LigneGrille {
  readonly libelle: string
  readonly max: number
  readonly actuel: number
  readonly refonte: number
  readonly certifie: number
}

export interface Certification {
  readonly nom: string
  readonly statut: string
  readonly points: number
  readonly intitule: string
  readonly description: string
  readonly code_rs: string
}

export interface DonneesRapport {
  readonly scores: Readonly<Record<'actuel' | 'refonte' | 'certifie', number>>
  readonly grille: {
    readonly bureautique: readonly LigneGrille[]
    readonly conformite: readonly LigneGrille[]
  }
  readonly certifications?: readonly Certification[]
  readonly candidat?: string
  readonly cible?: string
  readonly date?: string
  readonly organisme?: string
  readonly constat_ouverture?: string
  readonly detail_actuel?: string
  readonly detail_refonte?: string
  readonly detail_certifie?: string
  readonly etiq_actuel?: string
  readonly etiq_refonte?: string
  readonly etiq_certifie?: string
  readonly argumentaire?: string
  readonly corriges?: unknown
  readonly atouts?: unknown
  readonly chapo_certifications?: string
  readonly note_certifications?: string
  readonly plan_action?: unknown
  readonly citation?: string
  /** 0.6.2 : lien du CV en Google Docs, depose par le plugin dans « CV faits ». */
  readonly lien_google_doc?: unknown
  /** 0.6.2 : postes retires du CV pour qu'il tienne sur une page. */
  readonly experiences_ecartees?: unknown
}

export interface TotauxColonne {
  readonly bureautique: number
  readonly conformite: number
  readonly total: number
}

export type Totaux = Readonly<Record<Colonne, TotauxColonne>>

export type Verification =
  | { readonly coherent: true; readonly totaux: Totaux; readonly donnees: DonneesRapport }
  | { readonly coherent: false; readonly motif: string }

/**
 * `int(l.get(colonne, 0) or 0)` de Python, a l'identique.
 *
 * Tronque vers zero plutot que d'arrondir, et traite null, undefined, '' et NaN
 * comme zero. Etre PLUS strict que le generateur serait un piege : on refuserait
 * d'afficher un rapport qu'il a pourtant accepte de produire.
 */
function entier(valeur: unknown): number {
  if (valeur === null || valeur === undefined || valeur === '') return 0
  const nombre = Number(valeur)
  return Number.isFinite(nombre) ? Math.trunc(nombre) : 0
}

function somme(lignes: readonly LigneGrille[], colonne: Colonne): number {
  return lignes.reduce((cumul, ligne) => cumul + entier(ligne[colonne]), 0)
}

/** Une version de format inconnue n'est jamais rendue « au mieux ». */
export function versionSupportee(version: string | null | undefined): boolean {
  if (typeof version !== 'string') return false
  return (VERSIONS_RAPPORT_SUPPORTEES as readonly string[]).includes(version.trim())
}

export function verifierRapport(brut: unknown): Verification {
  if (brut === null || typeof brut !== 'object' || Array.isArray(brut)) {
    return { coherent: false, motif: "Le rapport n'est pas un objet." }
  }

  const donnees = brut as DonneesRapport
  const grille = donnees.grille ?? { bureautique: [], conformite: [] }
  const bureautique = Array.isArray(grille.bureautique) ? grille.bureautique : []
  const conformite = Array.isArray(grille.conformite) ? grille.conformite : []

  if (bureautique.length === 0 || conformite.length === 0) {
    return {
      coherent: false,
      motif: "La grille doit contenir les blocs 'bureautique' et 'conformite'.",
    }
  }

  const totaux = Object.fromEntries(
    COLONNES.map((colonne) => {
      const bureau = somme(bureautique, colonne)
      const confo = somme(conformite, colonne)
      return [colonne, { bureautique: bureau, conformite: confo, total: bureau + confo }]
    }),
  ) as Totaux

  if (totaux.max.total !== TOTAL_GRILLE_ATTENDU) {
    return {
      coherent: false,
      motif: `Le maximum de la grille vaut ${totaux.max.total} au lieu de ${TOTAL_GRILLE_ATTENDU}.`,
    }
  }

  const scores = donnees.scores ?? ({} as DonneesRapport['scores'])
  for (const cle of ['actuel', 'refonte', 'certifie'] as const) {
    // `int(scores.get(cle, -1))` : une cle absente vaut -1, donc ne peut pas
    // coincider avec un total, donc leve. Fidele au generateur.
    const annonce = scores[cle] === undefined ? -1 : entier(scores[cle])
    const calcule = totaux[cle].total
    if (annonce !== calcule) {
      return {
        coherent: false,
        motif: `Le score « ${cle} » annonce ${annonce} alors que la grille totalise ${calcule}.`,
      }
    }
  }

  if (totaux.certifie.bureautique <= totaux.refonte.bureautique) {
    return {
      coherent: false,
      motif: 'Le bloc bureautique certifie doit depasser le bloc non certifie.',
    }
  }

  return { coherent: true, totaux, donnees }
}

/**
 * Element d'une liste de constats, normalise.
 *
 * Le generateur emet deux formes selon la rubrique : `corriges` et `atouts`
 * portent des objets `{ titre, texte }`, `plan_action` des chaines. Rendre
 * directement l'une ou l'autre casse l'affichage — React refuse un objet comme
 * enfant, et la page entiere disparait sans message. C'est arrive.
 *
 * Cette fonction accepte les deux formes et ecarte silencieusement ce qui n'est
 * ni l'une ni l'autre : sur cette page, une rubrique manquante est preferable a
 * un ecran blanc en entretien. Le controle qui compte, celui des scores, reste
 * strict et bloquant.
 */
export interface Constat {
  readonly titre: string | null
  readonly texte: string
}

export function normaliserConstats(valeur: unknown): Constat[] {
  if (!Array.isArray(valeur)) return []

  return valeur.flatMap((element): Constat[] => {
    if (typeof element === 'string') {
      const texte = element.trim()
      return texte === '' ? [] : [{ titre: null, texte }]
    }

    if (element !== null && typeof element === 'object') {
      const objet = element as { titre?: unknown; texte?: unknown }
      const titre = typeof objet.titre === 'string' ? objet.titre.trim() : ''
      const texte = typeof objet.texte === 'string' ? objet.texte.trim() : ''
      if (titre === '' && texte === '') return []
      return [{ titre: titre === '' ? null : titre, texte: texte === '' ? titre : texte }]
    }

    return []
  })
}

/**
 * Lien du CV en Google Docs (plugin 0.6.2), ou `null`.
 *
 * Le lien vient de l'ingestion, donc d'un poste : il est traite comme une
 * donnee non sure. Seul un document Google Docs est accepte (https,
 * docs.google.com, /document/d/<identifiant>), et le lien est reconstruit a
 * partir de l'identifiant : aucun parametre ni fragment recu n'est repris. Un
 * lien `javascript:` ou vers un autre site ne s'affiche donc jamais.
 */
export function lienGoogleDoc(donnees: unknown): string | null {
  if (donnees === null || typeof donnees !== 'object') return null
  const brut = (donnees as { lien_google_doc?: unknown }).lien_google_doc
  if (typeof brut !== 'string') return null

  let url: URL
  try {
    url = new URL(brut.trim())
  } catch {
    return null
  }
  if (url.protocol !== 'https:' || url.hostname !== 'docs.google.com') return null

  const correspondance = /^\/document\/d\/([A-Za-z0-9_-]{20,})(?:\/|$)/.exec(url.pathname)
  if (correspondance === null) return null
  return `https://docs.google.com/document/d/${correspondance[1]}/edit`
}

/** Poste retire du CV pour tenir sur une page, normalise pour l'affichage. */
export interface ExperienceEcartee {
  readonly intitule: string
  readonly dates: string | null
  readonly motif: string | null
}

function texte(valeur: unknown): string {
  return typeof valeur === 'string' ? valeur.trim() : ''
}

/**
 * Meme principe que normaliserConstats : ce qui n'a pas la forme attendue est
 * ecarte sans bruit, une rubrique manquante vaut mieux qu'un ecran blanc.
 */
export function normaliserExperiencesEcartees(valeur: unknown): ExperienceEcartee[] {
  if (!Array.isArray(valeur)) return []

  return valeur.flatMap((element): ExperienceEcartee[] => {
    if (typeof element === 'string') {
      const intitule = element.trim()
      return intitule === '' ? [] : [{ intitule, dates: null, motif: null }]
    }
    if (element === null || typeof element !== 'object') return []

    const objet = element as Record<string, unknown>
    const intitule = [texte(objet.poste), texte(objet.employeur)].filter((x) => x !== '').join(' — ')
    if (intitule === '') return []
    const dates = [texte(objet.debut), texte(objet.fin)].filter((x) => x !== '').join(' – ')
    const motif = texte(objet.motif)
    return [{ intitule, dates: dates === '' ? null : dates, motif: motif === '' ? null : motif }]
  })
}
